"use client";

import { useState } from "react";
import { isCustomShortcode } from "@/lib/emoji/shortcode";

/**
 * ONE RENDERER for an emoji held as a VALUE — a reaction chip, a picker cell,
 * an autocomplete row, a "current reaction" button.
 *
 * This is the (B) half of the emoji story. The (A) half is an emoji inside a
 * cooked body, which is an `<img class="theta-emoji">` the server wrote and
 * `globals.css` sizes; React never touches those, and this component must not
 * try to. Merging the two would mean parsing server HTML into a React tree.
 *
 * WHY IT EXISTS. The same glyph was drawn six different ways at nine different
 * sizes, and two of those sat inside one popover: the "frequently used" row
 * drew 👍 as Twitter's artwork while the grid three pixels below drew the same
 * character as the OS font. Same for a reaction chip against the post body it
 * sat under.
 *
 * NO MORE TWEMOJI. A unicode emoji is now drawn the way the browser draws it,
 * everywhere. It means a chip matches the body beneath it and the picker cell
 * it was chosen from; it costs a per-platform difference that every post body
 * already had, so the app got more consistent with itself, not less. It also
 * takes a third-party CDN out of the render path and removes a whole class of
 * breakage — an emoji newer than the pinned twemoji 14.0.2 had no SVG and
 * rendered as a broken image.
 */

export type EmojiSize = "sm" | "md" | "lg" | "xl";

/**
 * Four names instead of the nine literals that were in use. Pixels rather than
 * the body's `em` token on purpose: these sit in fixed-height rows and pills,
 * where a size that follows the surrounding font would change the row's height.
 */
const SIZE_PX: Record<EmojiSize, number> = { sm: 16, md: 20, lg: 24, xl: 28 };

export function Emoji({
  emoji,
  url,
  size = "md",
  label,
  loading,
  className,
}: {
  /** A unicode character, or a bare custom shortcode without its colons. */
  emoji: string;
  /** The custom emoji's image, when one is known. */
  url?: string | null;
  size?: EmojiSize;
  /**
   * Overrides the derived alt/aria label. An empty string hides the glyph from
   * assistive tech — for a row where the shortcode is already visible text.
   */
  label?: string;
  /** Defer the image request until it scrolls near — for a grid of hundreds. */
  loading?: "lazy" | "eager";
  className?: string;
}) {
  // The url that failed, not a boolean: a fresh signed url after a refetch is
  // a new attempt, and a flag that never reset kept an instance in the
  // shortcode arm for good.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const px = SIZE_PX[size];
  const custom = isCustomShortcode(emoji);
  const text = label ?? (custom ? `:${emoji}:` : emoji);
  const hidden = label === "";

  // A MISSING url IS NOT "THEREFORE UNICODE" (OSO-431). It is also what a
  // pre-cutover reaction and an in-flight optimistic one both look like, so the
  // value decides, and the shortcode is the honest fallback: it is what the
  // author typed and what the composer shows while the picture loads.
  //
  // The failed arm is the one that did not exist at all. An expired signed
  // url, a deleted upload or a since-removed emoji each drew the browser's
  // broken image with nothing to fall back to. No `title`: every caller
  // already has a tooltip of its own, and two with the same text is noise.
  if (url && failedUrl !== url) {
    return (
      <img
        src={url}
        alt={text}
        aria-hidden={hidden || undefined}
        draggable={false}
        loading={loading}
        onError={() => setFailedUrl(url)}
        className={className ?? "object-contain inline-block align-middle"}
        style={{ width: px, height: px }}
      />
    );
  }

  if (custom) {
    return (
      <span
        aria-hidden={hidden || undefined}
        className={className ?? "inline-block align-middle font-medium"}
        style={{ fontSize: Math.max(10, Math.round(px * 0.6)), lineHeight: 1 }}
      >
        {text}
      </span>
    );
  }

  // The same fixed box as the image arm, so a unicode chip and a custom one
  // in one 20px pill share a footprint. A colour-emoji glyph is ~1.2em wide
  // and font-dependent, so the font is set a little under the box.
  return (
    <span
      role={hidden ? undefined : "img"}
      aria-label={hidden ? undefined : text}
      aria-hidden={hidden || undefined}
      className={className ?? "inline-flex items-center justify-center align-middle"}
      style={{ width: px, height: px, fontSize: Math.round(px * 0.85), lineHeight: 1 }}
    >
      {emoji}
    </span>
  );
}
